import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
let filtersDrawerCloseRequested = false;

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.facets__see-results, .facets-drawer__close')) {
      filtersDrawerCloseRequested = true;
      return;
    }

    if (event.target.closest('.facets-toggle__button, .facets__drawer-trigger')) {
      filtersDrawerCloseRequested = false;
    }
  },
  { capture: true }
);

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const drawerDialog = this.closest('dialog.facets--drawer');
    const shouldRestoreDrawer = drawerDialog instanceof HTMLDialogElement && drawerDialog.open;
    const drawerScrollTop = shouldRestoreDrawer
      ? drawerDialog.querySelector('.facets-drawer__filters')?.scrollTop || 0
      : 0;
    const lockedScrollY = this.#getLockedScrollY();
    const viewTransition = !drawerDialog;

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer
        .renderSection(this.sectionId)
        .then(() => {
          if (shouldRestoreDrawer) this.#restoreFiltersDrawer(drawerScrollTop, lockedScrollY);
        })
        .catch((error) => {
          console.error(error);
          if (shouldRestoreDrawer) this.#restoreFiltersDrawer(drawerScrollTop, lockedScrollY);
        });
    }
  }

  /**
   * Gets the page scroll position while the drawer has body scroll locked.
   * @returns {number}
   */
  #getLockedScrollY() {
    const fixedTop = Number.parseInt(document.body.style.top || '0', 10);
    return fixedTop < 0 ? Math.abs(fixedTop) : window.scrollY;
  }

  /**
   * Reopens the filter drawer after section rendering replaces the dialog markup.
   * @param {number} drawerScrollTop
   * @param {number} pageScrollY
   */
  #restoreFiltersDrawer(drawerScrollTop, pageScrollY) {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
    window.scrollTo({ top: pageScrollY, behavior: 'instant' });

    if (filtersDrawerCloseRequested) return;

    requestAnimationFrame(() => {
      const drawer = document.getElementById('filters-drawer');
      const showDialog = drawer?.showDialog;
      if (typeof showDialog !== 'function') return;

      showDialog.call(drawer);

      requestAnimationFrame(() => {
        const scrollArea = drawer.querySelector('.facets-drawer__filters');
        if (scrollArea) scrollArea.scrollTop = drawerScrollTop;
      });
    });
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLElement | undefined} rangeControl - The visual price range control
 * @property {HTMLElement | undefined} rangeTrack - The selected range track
 * @property {HTMLButtonElement | undefined} minThumb - The minimum price thumb
 * @property {HTMLButtonElement | undefined} maxThumb - The maximum price thumb
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;
  /** @type {string} */
  displayMoneyFormat;
  /** @type {number} */
  rangeMin = 0;
  /** @type {number} */
  rangeMax = 0;
  /** @type {'min'|'max'|null} */
  #activeThumb = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.displayMoneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.displayMoneyFormat);
    this.rangeMin = this.#toNumber(this.dataset.rangeMin, 0);
    this.rangeMax = this.#toNumber(this.dataset.rangeMax, 0);
    this.#bindInputEvents();
    this.#syncControlsFromInputs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointermove', this.#handlePointerMove);
    document.removeEventListener('pointerup', this.#handlePointerUp);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    if (event.target instanceof HTMLInputElement && event.target.type === 'range') return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Binds explicit listeners for controls that update while dragging or typing.
   */
  #bindInputEvents() {
    const { minInput, maxInput, rangeControl, minThumb, maxThumb } = this.refs;

    rangeControl?.addEventListener('pointerdown', this.#handlePointerDown);
    minThumb?.addEventListener('keydown', (event) => this.#handleThumbKeydown(event, 'min'));
    maxThumb?.addEventListener('keydown', (event) => this.#handleThumbKeydown(event, 'max'));

    minInput.addEventListener('input', () => this.#handleTextInput('min'));
    maxInput.addEventListener('input', () => this.#handleTextInput('max'));
    minInput.addEventListener('blur', () => this.updatePriceFilterAndResults());
    maxInput.addEventListener('blur', () => this.updatePriceFilterAndResults());
    minInput.addEventListener('keydown', (event) => this.#handleTextInputKeydown(event));
    maxInput.addEventListener('keydown', (event) => this.#handleTextInputKeydown(event));
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#syncControlsFromInputs();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into major units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in major units
   */
  #parseDisplayValue(displayValue, currency) {
    const numericValue = this.#toNumber(displayValue, Number.NaN);
    if (Number.isFinite(numericValue)) return numericValue;

    const minorUnits = convertMoneyToMinorUnits(displayValue, currency);
    return minorUnits == null ? 0 : minorUnits / 100;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    const min = this.rangeMin;
    const max = this.rangeMax;

    if (value < min) {
      input.value = String(min);
    } else if (value > max) {
      input.value = String(max);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;
    const min = this.#getInputValue(minInput, this.rangeMin);
    const max = this.#getInputValue(maxInput, this.rangeMax);

    minInput.max = String(max);
    maxInput.min = String(min);
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }

  /**
   * Handles manual number field editing.
   * @param {'min'|'max'} control
   */
  #handleTextInput(control) {
    const { minInput, maxInput } = this.refs;
    const input = control === 'min' ? minInput : maxInput;
    this.#adjustToValidValues(input);
    this.#syncControlsFromInputs();
  }

  /**
   * Applies manual number field edits on Enter.
   * @param {KeyboardEvent} event
   */
  #handleTextInputKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.updatePriceFilterAndResults();
  }

  /**
   * Syncs range thumbs and labels from text inputs.
   */
  #syncControlsFromInputs() {
    const { minInput, maxInput } = this.refs;
    const min = this.#getInputValue(minInput, this.rangeMin);
    const max = this.#getInputValue(maxInput, this.rangeMax);

    this.#updateRangeUI(min, max);
  }

  /**
   * Updates the selected range visuals and labels.
   * @param {number} min
   * @param {number} max
   */
  #updateRangeUI(min, max) {
    const { rangeTrack, minThumb, maxThumb } = this.refs;
    const span = Math.max(this.rangeMax - this.rangeMin, 1);
    const lower = ((min - this.rangeMin) / span) * 100;
    const upper = ((max - this.rangeMin) / span) * 100;

    rangeTrack?.parentElement?.style.setProperty('--price-range-lower', `${Math.max(0, Math.min(lower, 100))}%`);
    rangeTrack?.parentElement?.style.setProperty('--price-range-upper', `${Math.max(0, Math.min(upper, 100))}%`);

    this.#updateThumbA11y(minThumb, min);
    this.#updateThumbA11y(maxThumb, max);
  }

  /**
   * Handles pointer start for the custom range control.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    const { minThumb, maxThumb } = this.refs;
    if (!(event.target instanceof Element)) return;

    event.preventDefault();

    if (event.target === minThumb) {
      this.#activeThumb = 'min';
    } else if (event.target === maxThumb) {
      this.#activeThumb = 'max';
    } else {
      const value = this.#getValueFromPointer(event);
      const currentMin = this.#getInputValue(this.refs.minInput, this.rangeMin);
      const currentMax = this.#getInputValue(this.refs.maxInput, this.rangeMax);
      this.#activeThumb = Math.abs(value - currentMin) <= Math.abs(value - currentMax) ? 'min' : 'max';
      this.#setThumbValue(this.#activeThumb, value);
    }

    document.addEventListener('pointermove', this.#handlePointerMove);
    document.addEventListener('pointerup', this.#handlePointerUp, { once: true });
  };

  /**
   * Handles pointer movement for the custom range control.
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (!this.#activeThumb) return;

    event.preventDefault();
    this.#setThumbValue(this.#activeThumb, this.#getValueFromPointer(event));
  };

  /**
   * Commits custom range control changes.
   */
  #handlePointerUp = () => {
    document.removeEventListener('pointermove', this.#handlePointerMove);

    if (this.#activeThumb) {
      this.updatePriceFilterAndResults();
    }

    this.#activeThumb = null;
  };

  /**
   * Handles keyboard interaction on slider thumbs.
   * @param {KeyboardEvent} event
   * @param {'min'|'max'} control
   */
  #handleThumbKeydown(event, control) {
    const keys = ['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();

    const step = event.shiftKey ? 10 : 1;
    const current = this.#getInputValue(control === 'min' ? this.refs.minInput : this.refs.maxInput, control === 'min' ? this.rangeMin : this.rangeMax);
    let next = current;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= step;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += step;
    if (event.key === 'Home') next = this.rangeMin;
    if (event.key === 'End') next = this.rangeMax;

    this.#setThumbValue(control, next);
    this.updatePriceFilterAndResults();
  }

  /**
   * Converts pointer position to the nearest valid price value.
   * @param {PointerEvent} event
   * @returns {number}
   */
  #getValueFromPointer(event) {
    const { rangeControl } = this.refs;
    if (!rangeControl) return this.rangeMin;

    const rect = rangeControl.getBoundingClientRect();
    const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
    const clampedRatio = Math.max(0, Math.min(ratio, 1));
    const rawValue = this.rangeMin + clampedRatio * (this.rangeMax - this.rangeMin);

    return Math.round(rawValue);
  }

  /**
   * Updates a range thumb and synced input value.
   * @param {'min'|'max'} control
   * @param {number} value
   */
  #setThumbValue(control, value) {
    const { minInput, maxInput } = this.refs;
    const currentMin = this.#getInputValue(minInput, this.rangeMin);
    const currentMax = this.#getInputValue(maxInput, this.rangeMax);
    let min = currentMin;
    let max = currentMax;

    if (control === 'min') {
      min = Math.max(this.rangeMin, Math.min(value, max));
    } else {
      max = Math.min(this.rangeMax, Math.max(value, min));
    }

    minInput.value = min <= this.rangeMin ? '' : String(Math.round(min));
    maxInput.value = max >= this.rangeMax ? '' : String(Math.round(max));
    this.#updateRangeUI(min, max);
  }

  /**
   * Updates slider thumb accessibility attributes.
   * @param {HTMLButtonElement | undefined} thumb
   * @param {number} value
   */
  #updateThumbA11y(thumb, value) {
    if (!thumb) return;

    const roundedValue = Math.round(value);
    thumb.setAttribute('aria-valuenow', String(roundedValue));
    thumb.setAttribute('aria-valuetext', this.#formatDisplayMoney(roundedValue));
  }

  /**
   * Gets a normalized numeric input value.
   * @param {HTMLInputElement} input
   * @param {number} fallback
   * @returns {number}
   */
  #getInputValue(input, fallback) {
    if (input.value.trim() === '') return fallback;

    const value = this.#parseDisplayValue(input.value, this.currency);
    return Math.max(this.rangeMin, Math.min(value, this.rangeMax));
  }

  /**
   * Converts a string value to a number.
   * @param {string | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  #toNumber(value, fallback) {
    if (value == null || value === '') return fallback;

    const normalized = value.replace(/\s/g, '').replace(',', '.');
    const number = Number.parseFloat(normalized);

    return Number.isFinite(number) ? number : fallback;
  }

  /**
   * Formats a major-unit value for the slider labels.
   * @param {number} value
   * @returns {string}
   */
  #formatDisplayMoney(value) {
    return formatMoney(Math.round(value * 100), this.displayMoneyFormat, this.currency);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
